/**
 * VideoMatchModal — drop camera files on the Upload page and let the server
 * work out which session each one was recorded in.
 *
 * "Add Video" on a session assumes you already have the session open. This
 * is the other way round: a folder of GoPro chapters or dashcam clips from a
 * track day, matched to the day's sessions by the GPS in the video — position
 * and the satellite clock, which is right even when the camera's clock was
 * never set — and confirmed by you before anything is attached.
 *
 * Step 1: pick files (+ optionally narrow the search by car / date). Each is
 *         streamed to staging and fingerprinted on arrival.
 * Step 2: one row per recording (GoPro chapters are grouped), each with its
 *         best-match session preselected when the server is sure, the
 *         evidence it used, and a dropdown of the runners-up. A SmartyCam3
 *         file that matches nothing can create a new session instead.
 * Step 3: attach the confirmed pairs. The primary action then becomes
 *         "Set up overlay & render", which opens the usual render wizard at
 *         the layout editor for the attached session (per-row links when
 *         several sessions were attached) — attaching alone changes nothing
 *         the user can see, so this is the natural next step, not "Done".
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  attachStagedVideos, createSessionFromStagedVideo, deleteStagedVideo,
  uploadVideoStaging, videoMatch,
} from '../../services/api';
import styles from './EcuMatchModal.module.css';

const MAX_FILES = 20;
const VIDEO_EXTS = ['.mp4', '.mov'];
const CREATE_NEW = '__new__';

export function isVideoFile(file) {
  const n = (file?.name || '').toLowerCase();
  return VIDEO_EXTS.some(ext => n.endsWith(ext));
}

const VERDICT_UI = {
  confident: { label: 'Confident', color: '#3ddc84' },
  likely: { label: 'Likely', color: '#f5c842' },
  uncertain: { label: 'Uncertain', color: '#ff9b6b' },
  no_candidates: { label: 'No match', color: '#ff6b6b' },
  error: { label: 'Unreadable', color: '#ff6b6b' },
};

const KIND_LABEL = {
  gopro: 'GoPro', viofo: 'VIOFO', smartycam3: 'SmartyCam3', smartycam_mov: 'SmartyCam', other: 'Video',
};

function confColor(c) {
  if (c == null) return '#888';
  if (c >= 0.8) return '#3ddc84';
  if (c >= 0.7) return '#f5c842';
  return '#ff6b6b';
}

function fmtDuration(s) {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s - m * 60)).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// session_date is the circuit's wall clock stamped as UTC, so it must be
// shown as UTC or the browser's zone shifts it.
function fmtWallClock(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}

function candidateLabel(c) {
  const bits = [fmtWallClock(c.session_date), c.track, c.label].filter(Boolean);
  const seen = new Set();
  const uniq = bits.filter(b => (seen.has(b) ? false : seen.add(b)));
  return `${uniq.join(' · ')} — ${fmtDuration(c.duration)}, ${c.lap_count} laps`;
}

function sourceLabel(r) {
  if (r.probe === 'ffprobe') return 'Timestamps only — video service unavailable';
  const when = r.utc_start ? fmtDate(r.utc_start) : null;
  if (r.clock_source === 'gps' || r.clock_source === 'xrk_gps') {
    return `GPS · ${when}`;
  }
  if (r.clock_source === 'creation_time') {
    return `${r.gps_source !== 'none' ? 'GPS, ' : 'No GPS, '}camera clock ${when}`;
  }
  return 'No clock or GPS in this file';
}

function recordingName(r) {
  const first = r.filenames[0];
  const extra = r.filenames.length - 1;
  return extra > 0 ? `${first} + ${extra} chapter${extra > 1 ? 's' : ''}` : first;
}

export default function VideoMatchModal({ initialFiles, onClose, onAttached, onSessionCreated, onRender }) {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState(() => Array.from(initialFiles || []).filter(isVideoFile));
  const [vehicle, setVehicle] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);   // { pct, label }
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [stagedIds, setStagedIds] = useState([]);
  // recording key (first filename) -> session_id | CREATE_NEW | '' (skip)
  const [choice, setChoice] = useState({});
  // recording key -> { state: 'working'|'done'|'error', msg, sessionId }
  const [status, setStatus] = useState({});
  const inputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    const usable = Array.from(incoming || []).filter(isVideoFile);
    if (!usable.length) {
      setError('Those files don’t look like videos (.mp4 or .mov).');
      return;
    }
    setError(null);
    setFiles(prev => {
      const byName = new Map(prev.map(f => [f.name, f]));
      usable.forEach(f => byName.set(f.name, f));
      return Array.from(byName.values()).slice(0, MAX_FILES);
    });
  }, []);

  const applyResults = useCallback((resp) => {
    setResults(resp.results);
    const init = {};
    resp.results.forEach(r => {
      // Preselect only when the server is sure enough to stand behind it.
      const auto = (r.verdict === 'confident' || r.verdict === 'likely') && r.candidates[0];
      if (auto) init[r.filenames[0]] = r.candidates[0].session_id;
      else if (r.kind === 'smartycam3' && !r.candidates.some(c => c.confidence > 0)) init[r.filenames[0]] = CREATE_NEW;
      else init[r.filenames[0]] = '';
    });
    setChoice(init);
    setStatus({});
  }, []);

  // ── Step 1 → 2: stage every file, then one match call ─────────────────────
  const runMatch = useCallback(async () => {
    setBusy(true); setError(null);
    const totalBytes = files.reduce((n, f) => n + f.size, 0);
    let doneBytes = 0;
    const ids = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        const staged = await uploadVideoStaging(f, (loaded, detail) => {
          const pct = totalBytes ? Math.round(((doneBytes + loaded) / totalBytes) * 100) : 0;
          const label = detail?.status === 'finalizing'
            ? `Reading GPS from ${f.name}…`
            : detail?.status === 'retrying'
              ? `Retrying ${f.name} (${detail.attempt}/${detail.maxAttempts})…`
              : `Uploading ${f.name} (${i + 1}/${files.length})… ${pct}%`;
          setProgress({ pct, label });
        });
        doneBytes += f.size;
        ids.push(staged.staging_id);
      }
      setStagedIds(ids);
      setProgress({ pct: 100, label: 'Comparing against your sessions…' });
      const resp = await videoMatch({ stagingIds: ids, vehicle, dateFrom, dateTo });
      applyResults(resp);
      setStep(2);
    } catch (e) {
      setError(String(e.message || e));
      // Anything staged so far is on the server; the sweep would collect it,
      // but there is no reason to wait for that.
      ids.forEach(id => deleteStagedVideo(id).catch(() => {}));
    } finally {
      setBusy(false); setProgress(null);
    }
  }, [files, vehicle, dateFrom, dateTo, applyResults]);

  const rerunMatch = useCallback(async (reprobe) => {
    setBusy(true); setError(null);
    try {
      applyResults(await videoMatch({ stagingIds: stagedIds, vehicle, dateFrom, dateTo, reprobe }));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [stagedIds, vehicle, dateFrom, dateTo, applyResults]);

  const pending = useMemo(
    () => results.filter(r => choice[r.filenames[0]] && status[r.filenames[0]]?.state !== 'done'),
    [results, choice, status]);

  // ── Step 2 → 3: attach each confirmed pair ────────────────────────────────
  const runAttach = useCallback(async () => {
    setBusy(true); setError(null);
    let attached = 0;
    for (const r of pending) {
      const key = r.filenames[0];
      const target = choice[key];
      setStatus(s => ({ ...s, [key]: { state: 'working' } }));
      try {
        if (target === CREATE_NEW) {
          const resp = await createSessionFromStagedVideo(r.staging_ids[0]);
          setStatus(s => ({ ...s, [key]: { state: 'done', msg: 'new session created', sessionId: resp.session_id } }));
          onSessionCreated?.(resp);
        } else {
          const resp = await attachStagedVideos(target, r.staging_ids);
          const n = resp.attached.length;
          const msg = n ? `${n} file${n === 1 ? '' : 's'} attached`
            : (resp.skipped[0]?.reason === 'already_attached' ? 'already attached' : 'nothing attached');
          setStatus(s => ({ ...s, [key]: { state: 'done', msg, sessionId: target } }));
          attached += n;
        }
      } catch (e) {
        setStatus(s => ({ ...s, [key]: { state: 'error', msg: String(e.message || e) } }));
      }
    }
    setBusy(false);
    if (attached) onAttached?.(attached);
  }, [pending, choice, onAttached, onSessionCreated]);

  const anyDegraded = results.some(r => r.probe === 'ffprobe');
  // Sessions that now have video, in the order they were attached. Attaching
  // is only half the job — the overlay still has to be laid out and rendered
  // — so once everything is attached the primary action carries the user
  // straight into the layout editor rather than dropping them back on the
  // session list with nothing visibly changed.
  const attachedSessions = useMemo(() => {
    const seen = [];
    results.forEach(r => {
      const st = status[r.filenames[0]];
      if (st?.state === 'done' && st.sessionId && !seen.includes(st.sessionId)) seen.push(st.sessionId);
    });
    return seen;
  }, [results, status]);
  const allSettled = results.length > 0 && results.every(r => {
    const st = status[r.filenames[0]];
    return st?.state === 'done' || st?.state === 'error' || !choice[r.filenames[0]];
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Match Videos to Sessions
            <span className={styles.subtitle}>
              {step === 1 ? ' — find the right session automatically' : ` — ${results.length} recording${results.length === 1 ? '' : 's'}`}
            </span>
          </h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close" disabled={busy}>×</button>
        </div>

        {error && <div className={styles.errorBar}>{error}</div>}

        {step === 1 && (
          <div className={styles.body}>
            <p className={styles.hint}>
              Drop your camera files — GoPro chapters, dashcam clips, SmartyCam
              recordings. Each is matched to the session it was recorded in using
              the GPS inside the video, so it works even when the camera&apos;s clock
              was never set. You confirm before anything is attached.
            </p>

            {busy ? (
              <div className={styles.dropzone}>
                <span className={styles.dropHint}>{progress?.label || 'Working…'}</span>
                <div className={styles.progressBar}>
                  <div className={`${styles.progressFill} ${progress?.pct >= 100 ? styles.progressIndeterminate : ''}`}
                       style={progress && progress.pct < 100 ? { width: `${progress.pct}%` } : undefined} />
                </div>
              </div>
            ) : (
              <div
                className={styles.dropzone}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              >
                <span className={styles.dropHint}>
                  {files.length
                    ? `${files.length} file${files.length > 1 ? 's' : ''} ready`
                    : 'Drop MP4/MOV files here or click to browse'}
                </span>
                <span className={styles.dropSub}>Up to {MAX_FILES} at once · chapters of one recording are grouped</span>
              </div>
            )}
            {/* Outside the dropzone: a programmatic .click() bubbles, and as a
                child it would re-trigger the dropzone's own onClick. */}
            <input
              ref={inputRef} type="file" multiple hidden
              accept=".mp4,.mov,video/mp4,video/quicktime"
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />

            {files.length > 0 && !busy && (
              <ul className={styles.fileList}>
                {files.map(f => (
                  <li key={f.name} className={styles.fileItem}>
                    <span className={styles.fileName}>{f.name}</span>
                    <span className={styles.fileSize}>{(f.size / 1e9).toFixed(2)} GB</span>
                    <button
                      className={styles.removeBtn}
                      onClick={() => setFiles(prev => prev.filter(x => x.name !== f.name))}
                      aria-label={`Remove ${f.name}`}
                    >×</button>
                  </li>
                ))}
              </ul>
            )}

            <details className={styles.narrow}>
              <summary className={styles.narrowSummary}>Narrow the search (optional)</summary>
              <p className={styles.narrowHint}>
                Only needed for a video with no GPS — those match on the camera&apos;s
                clock alone, and a wrong clock can pick a wrong day.
              </p>
              <div className={styles.narrowRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Car</span>
                  <input className={styles.input} value={vehicle} placeholder="any"
                         onChange={e => setVehicle(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>From</span>
                  <input className={styles.input} type="date" value={dateFrom}
                         onChange={e => setDateFrom(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>To</span>
                  <input className={styles.input} type="date" value={dateTo}
                         onChange={e => setDateTo(e.target.value)} />
                </label>
              </div>
            </details>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
              <button className={styles.primaryBtn} onClick={runMatch}
                      disabled={busy || !files.length}>
                Find sessions
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.body}>
            <p className={styles.hint}>
              Each recording is paired with the session it was made in. Change any
              pairing below, or set it to <em>Skip</em>.
            </p>
            {anyDegraded && (
              <div className={styles.errorBar}>
                The video service was unavailable, so some files were read by their
                timestamps only.{' '}
                <button className={styles.linkBtn} onClick={() => rerunMatch(true)} disabled={busy}>
                  Retry with GPS
                </button>
              </div>
            )}

            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Recording</th>
                    <th>Length</th>
                    <th>Session</th>
                    <th className={styles.thMono}>Match</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map(r => {
                    const key = r.filenames[0];
                    const st = status[key];
                    const v = VERDICT_UI[r.verdict] || VERDICT_UI.uncertain;
                    const top = r.candidates[0];
                    const chosen = r.candidates.find(c => c.session_id === choice[key]);
                    const usable = r.candidates.filter(c => c.confidence > 0);
                    const listed = usable.length ? usable : r.candidates;
                    return (
                      <tr key={key}>
                        <td>
                          <div className={styles.logName} title={r.filenames.join('\n')}>{recordingName(r)}</div>
                          <div className={styles.logMeta}>
                            {KIND_LABEL[r.kind] || 'Video'} · {sourceLabel(r)}
                            {r.considered ? ` · ${r.considered} sessions checked` : ''}
                          </div>
                        </td>
                        <td className={styles.tdMono}>{fmtDuration(r.duration_s)}</td>
                        <td>
                          {(listed.length || r.kind === 'smartycam3') ? (
                            <select
                              className={styles.select}
                              value={choice[key] ?? ''}
                              disabled={busy || st?.state === 'done'}
                              onChange={e => setChoice(c => ({ ...c, [key]: e.target.value }))}
                            >
                              <option value="">Skip this recording</option>
                              {listed.map(c => (
                                <option key={c.session_id} value={c.session_id}>
                                  {candidateLabel(c)}
                                  {c.has_gopro_files || c.has_video ? ' (already has video)' : ''}
                                </option>
                              ))}
                              {r.kind === 'smartycam3' && (
                                <option value={CREATE_NEW}>Create a new session from this file</option>
                              )}
                            </select>
                          ) : (
                            <span className={styles.noMatch}>
                              {r.error || 'No session found for this recording'}
                            </span>
                          )}
                          {chosen?.evidence && (
                            <div className={styles.logMeta}>{chosen.evidence}</div>
                          )}
                        </td>
                        <td className={styles.tdMono}>
                          <span className={styles.verdict} style={{ color: v.color }}>{v.label}</span>
                          {top && top.confidence > 0 && (
                            <span className={styles.conf} style={{ color: confColor(top.confidence) }}>
                              {Math.round(top.confidence * 100)}%
                            </span>
                          )}
                        </td>
                        <td className={styles.tdActions}>
                          {st?.state === 'done' && (
                            <>
                              <span className={styles.ok}>✓ {st.msg}</span>
                              {st.sessionId && onRender && attachedSessions.length > 1 && (
                                <button className={styles.linkBtn} onClick={() => onRender(st.sessionId)} disabled={busy}>
                                  Set up overlay &amp; render
                                </button>
                              )}
                            </>
                          )}
                          {st?.state === 'working' && <span className={styles.working}>attaching…</span>}
                          {st?.state === 'error' && <span className={styles.bad} title={st.msg}>failed</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className={styles.tableHint}>
              <strong>Match</strong> is how closely the video&apos;s GPS track follows
              the session&apos;s car at the same moment. &ldquo;Confident&rdquo; means
              the camera was in that car during that session; anything less is
              preselected only when nothing else comes close.
            </p>

            <div className={styles.footer}>
              <button className={styles.secondaryBtn} onClick={() => setStep(1)} disabled={busy}>
                Back
              </button>
              {allSettled && attachedSessions.length > 0 && onRender ? (
                <button className={styles.primaryBtn} onClick={() => onRender(attachedSessions[0])} disabled={busy}>
                  Set up overlay &amp; render →
                </button>
              ) : allSettled ? (
                <button className={styles.primaryBtn} onClick={onClose}>Done</button>
              ) : (
                <button className={styles.primaryBtn} onClick={runAttach}
                        disabled={busy || !pending.length}>
                  {busy ? 'Attaching…' : `Attach ${pending.length} recording${pending.length === 1 ? '' : 's'}`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
